import type {LogsEvent} from '@datadog/browser-logs';

import {environment} from '@environments/environment';

/**
 * Shared URL helpers for the frontend telemetry stack (OpenTelemetry web tracing, Datadog RUM,
 * Datadog Logs). Two concerns live here, both of which every pipeline must handle identically:
 * matching the OTLP collector endpoint so it can be excluded, and dropping query strings before
 * telemetry leaves the browser so secrets passed as query params (e.g. `/dashboard?token=…`) never
 * reach Datadog or the collector.
 */

/**
 * Matches the OTLP collector endpoint (`${otelCollectorUrl}` itself and anything under it). The
 * `(\/|$)` boundary keeps the match anchored to the `/otel` path segment so it can't also match
 * unrelated URLs that merely contain "otel".
 *
 * Callers: `otel.ts` (ignore the SDK's own export traces) and `datadog-rum.ts` (exclude those same
 * exporter POSTs from page activity).
 */
export function collectorUrlPattern(): RegExp {
  return new RegExp(environment.otelCollectorUrl + '(\\/|$)');
}

/**
 * Drops the query string (and anything after it) from a URL, returning just the path portion.
 * The primitive the event sanitizers below and the OTel span/navigation strippers all build on.
 */
export function stripQueryString(url: string): string {
  return url.split('?')[0];
}

/**
 * Sanitizes the `view` block shared by Datadog Logs and RUM events, in place.
 *
 * Both SDKs derive `view` from the same common event schema, so this is the one URL-bearing block
 * the two pipelines have in common; centralizing it stops them drifting apart. Typed structurally
 * rather than against `LogsEvent`/`RumEvent` to keep the shared core free of SDK coupling.
 *
 * `url` is required on both schemas, `referrer` optional — so it must not be synthesized when absent.
 */
export function stripViewUrls(view: {url: string; referrer?: string}): void {
  view.url = stripQueryString(view.url);
  if (view.referrer) {
    view.referrer = stripQueryString(view.referrer);
  }
}

/**
 * Sanitizes every URL a Datadog Logs event carries, in place. Backs the `datadogLogs.init`
 * `beforeSend` hook, and lives here rather than inline in `main.ts` because bootstrap runs its work
 * as an import side effect and so cannot be unit-tested.
 *
 * Past the shared `view` block, a `LogsEvent`'s only other URL is `http`, present on network-error
 * logs alone. (RUM's equivalent tail is `error.resource.url` — see `datadog-rum.ts`.)
 */
export function stripLogsEventUrls(event: LogsEvent): void {
  stripViewUrls(event.view);
  if (event.http?.url) {
    event.http.url = stripQueryString(event.http.url);
  }
}
